import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../api/client";
import type { Bought, CheckSummary, QueueStatus } from "../api/types";
import { Card, toast } from "../components/Bits";
import { shortTitle } from "../lib/fit";
import { useApp } from "../state";

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const QUICK: { v: Bought; label: string; status: QueueStatus; said: string }[] = [
  { v: "yes", label: "Bought", status: "bought", said: "Moved to the top. Tell us how it fits once it arrives." },
  { v: "not_yet", label: "Still deciding", status: "not_yet", said: "Kept in your queue." },
  { v: "no", label: "Decided not to", status: "passed", said: "Moved to Passed on." },
];

export function History() {
  const { meta, newBoard } = useApp();
  const nav = useNavigate();
  const [checks, setChecks] = useState<CheckSummary[] | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    api.checks().then(
      (x) => setChecks(x.checks),
      (e) => setErr(errorMessage(e)),
    );
  }, []);
  const all = checks ?? [];
  const bought = all.filter((c) => c.status === "bought");
  const deciding = all.filter((c) => c.status === "new" || c.status === "not_yet");
  const done = all.filter((c) => c.status === "reported");
  const passed = all.filter((c) => c.status === "passed");

  // The quick answers are the report's first question, so the report picks up from here.
  const mark = async (c: CheckSummary, v: Bought, status: QueueStatus, said: string) => {
    if (busy) return;
    setBusy(c.id);
    try {
      await api.report(c.id, { bought: v });
      setChecks((xs) => (xs ?? []).map((x) => (x.id === c.id ? { ...x, status, has_report: true } : x)));
      toast(said);
    } catch (e) {
      toast(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const row = (c: CheckSummary, kind: "queue" | "done" | "passed") => (
    <div className={"histrow" + (kind === "passed" ? " off" : "")} key={c.id}>
      <span className="t">
        <span className="br">{c.candidate.brand}</span>
        <span className="nm">{shortTitle(c.candidate.title)}</span>
      </span>
      {kind === "done" ? (
        <button className="chip sm lite" type="button" onClick={() => nav(`/check/${c.id}/report`)}>
          Your report
        </button>
      ) : (
        <span className="acts">
          <button className="chip sm" type="button" onClick={() => nav(`/check/${c.id}/report`)}>
            Update
          </button>
          {kind === "passed" && (
            <button className="chip sm lite" type="button" disabled={busy === c.id} onClick={() => void mark(c, "not_yet", "not_yet", "Back in your queue.")}>
              Undo
            </button>
          )}
        </span>
      )}
      {kind === "queue" && (
        <span className="quick">
          {QUICK.map((q) => (
            <button
              key={q.v}
              className="chip sm"
              type="button"
              aria-pressed={q.status === "bought" ? c.status === "bought" : q.v === "not_yet" ? c.status === "not_yet" : false}
              disabled={busy === c.id}
              onClick={() => void mark(c, q.v, q.status, q.said)}
            >
              {q.label}
            </button>
          ))}
        </span>
      )}
      <span className="s">
        {meta.categories[c.category]?.one ?? c.category} · checked {fmtDate(c.created_at)}
        {c.times_checked > 1 && ` · checked ${c.times_checked} times`} ·{" "}
        <button className="linkish" type="button" style={{ fontSize: "inherit" }} onClick={() => nav(`/check/${c.id}`)}>
          See the fit notes
        </button>
      </span>
    </div>
  );
  const waiting = bought.length + deciding.length;
  return (
    <section className="view enter">
      <h1>
        <span className="ph">Everything</span> <span className="ph">you've <em>checked</em></span>
      </h1>
      {err && <p className="hint warn">{err}</p>}
      {!checks && !err && <p className="eyebrow">Loading…</p>}
      {checks && !checks.length && (
        <Card tag="Nothing yet" mark="0">
          <p className="aside">Paste a link to something you're eyeing and it shows up here.</p>
          <button className="btn primary" type="button" onClick={newBoard}>
            Check an item
          </button>
        </Card>
      )}
      {waiting > 0 && (
        <Card tag="Waiting on your fit report" mark={waiting}>
          {bought.length > 0 && (
            <div>
              <p className="histsub">Bought, tell us how it fits</p>
              {bought.map((c) => row(c, "queue"))}
            </div>
          )}
          {deciding.length > 0 && (
            <div>
              {bought.length > 0 && <p className="histsub">Still deciding</p>}
              {deciding.map((c) => row(c, "queue"))}
            </div>
          )}
        </Card>
      )}
      {done.length > 0 && (
        <Card tag="Fit history" mark={done.length}>
          <div>{done.map((c) => row(c, "done"))}</div>
        </Card>
      )}
      {passed.length > 0 && (
        <Card tag="Passed on" mark={passed.length}>
          <div>{passed.map((c) => row(c, "passed"))}</div>
        </Card>
      )}
    </section>
  );
}
